import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Package as PackageIcon, Plus, QrCode, Square,
  PackageOpen, Send, ListOrdered, Settings as SettingsIcon, LogOut, Menu, X, Store, Users,
} from "lucide-react";

const NAV = [
  { label: "Overview", items: [
    { to: "/store", end: true, icon: LayoutDashboard, label: "Dashboard" },
  ]},
  { label: "Products", items: [
    { to: "/store/products", icon: PackageIcon, label: "Products" },
    { to: "/store/products/new", icon: Plus, label: "Add Product" },
  ]},
  { label: "QR Codes", items: [
    { to: "/store/qr-generator", icon: QrCode, label: "QR Code Generator" },
    { to: "/store/qr/blank", icon: Square, label: "Blank QR Codes" },
    { to: "/store/qr/assigned", icon: PackageOpen, label: "Assigned QR Codes" },
  ]},
  { label: "Packages", items: [
    { to: "/store/create-package", icon: Send, label: "Create Package" },
    { to: "/store/packages", icon: ListOrdered, label: "My Packages" },
  ]},
  { label: "Team", items: [
    { to: "/store/dispatchers", icon: Users, label: "Package Dispatchers" },
    { to: "/store/dispatcher-activity", icon: ListOrdered, label: "Dispatcher Activity" },
  ]},
  { label: "System", items: [
    { to: "/store/settings", icon: SettingsIcon, label: "Settings" },
  ]},
];

export default function StoreLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [store, setStore] = useState(null);

  useEffect(() => {
    api.get("/store/me").then(r => setStore(r.data.store)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavBody = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
        <Logo className="h-11" />
        <button
          data-testid="store-sidebar-close-btn"
          onClick={() => setMobileOpen(false)}
          className="md:hidden text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-6">
        {NAV.map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-2 text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">
              {group.label}
            </div>
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`store-sidebar-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      [
                        "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                        isActive
                          ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                          : "text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-100 hover:dark:bg-slate-900 border border-transparent",
                      ].join(" ")
                    }
                  >
                    <item.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-slate-100/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
          <div className="w-8 h-8 rounded-full bg-cyan-400 text-slate-950 grid place-items-center font-display font-bold">
            {(store?.store_name || user?.name || "S")[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{store?.store_name || "Store"}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <Store className="w-4 h-4 text-cyan-400" />
        </div>
        <button
          data-testid="store-logout-btn"
          onClick={handleLogout}
          className="mt-2 w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-100 hover:dark:bg-slate-900 rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white flex">
      <aside className="hidden md:flex w-64 shrink-0 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex-col fixed inset-y-0 left-0 z-30">
        <NavBody />
      </aside>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800">
            <NavBody />
          </div>
        </div>
      )}

      <div className="flex-1 md:ml-64 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 glass border-b border-slate-200 dark:border-slate-800 px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="store-sidebar-open-btn"
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-slate-700 dark:text-slate-300 hover:text-slate-900 hover:dark:text-white"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500">
              / Store Console · <span className="text-cyan-400">{store?.store_name || "—"}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono-tech text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="hidden sm:inline">ONLINE</span>
            <ThemeToggle size="sm" />
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
